import {
    useLayoutEffect,
    createContext,
    useContext,
    useEffect
} from "react";
import type {
    ChartContextType,
    ChartPartKind,
    ChartModel
} from "./type";
import {
    warnOnce
} from "../../utils/development";

export const CHART_PART_NAMES: Record<ChartPartKind | "plot" | "legend" | "table" | "tableToggle" | "value" | "range" | "skeleton", string> = {
    tableToggle: "TableToggle",
    skeleton: "Skeleton",
    content: "Content",
    footer: "Footer",
    header: "Header",
    legend: "Legend",
    range: "Range",
    table: "Table",
    value: "Value",
    plot: "Plot"
};

export const ChartContext = createContext<ChartContextType | undefined>(undefined);

export const useChartContext = ({
    part
}: {
    part: keyof typeof CHART_PART_NAMES;
}): ChartContextType | undefined => {
    const context = useContext(ChartContext);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: `Chart.${CHART_PART_NAMES[part]}: must be rendered inside <Chart>.`
        });
    }, [
        context,
        part
    ]);

    return context;
};

export const useChartModel = ({
    model
}: {
    model: ChartModel;
}) => {
    const context = useContext(ChartContext);

    const notifyModel = context?.notifyModel;

    const modelRef = context?.modelRef;

    useLayoutEffect(() => {
        if(modelRef) {
            modelRef.current = model;
        }
    });

    useEffect(() => {
        notifyModel?.();
    }, [
        model.legendItems?.map((item) => `${item.key}${item.title}`).join(","),
        model.data,
        model.seriesKeys.join(","),
        model.categoryKey,
        model.isShowTotal,
        model.isStacked,
        model.kind,
        notifyModel
    ]);

    useEffect(() => {
        return () => {
            if(modelRef) {
                modelRef.current = undefined;
            }

            notifyModel?.();
        };
    }, [
        modelRef,
        notifyModel
    ]);
};
